if (!customElements.get('compound-variant-picker')) {
  class CompoundVariantPicker extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;

      this.inputs = [...this.querySelectorAll('.ec-compound-source input[type="radio"]')];
      if (!this.inputs.length) return;

      this.records = this.inputs.map((input) => this.parseRecord(input));
      this.colorContainer = this.querySelector('[data-compound-colors]');
      this.styleContainer = this.querySelector('[data-compound-styles]');
      this.renderChoices();
      this.inputs.forEach((input) => input.addEventListener('change', () => this.sync()));
      this.dataset.ready = 'true';
      this.sync();
    }

    parseRecord(input) {
      const raw = input.value.trim();
      const parts = raw.split(' — ');
      let color = parts[0].trim();
      let style = parts.slice(1).join(' — ').trim();

      if (this.dataset.productHandle === 'spicy-girl-sexy-tight-corset-lace-hollow-sleeveless-strapless-top-2025-summer-fashion-women-street-trendy-vest') {
        // Display names follow what the photos show; raw option values stay untouched (DSers mapping).
        const passionMap = {
          'white': ['White', 'Gold Busk'],
          'black': ['Black', 'Gold Busk'],
          'beige': ['Apricot', 'Gold Busk'],
          'ivory': ['Beige', 'Gold Busk'],
          'floral apricot': ['Ivory', 'Smooth Front'],
          'black — floral': ['Black', 'Smooth Front'],
        };
        const mapped = passionMap[raw.toLowerCase()];
        if (mapped) [color, style] = mapped;
      } else if (!style) {
        style = 'Classic';
      }

      return {
        input,
        raw,
        color,
        style,
        available: !input.classList.contains('disabled') && !input.disabled,
      };
    }

    uniqueValues(key) {
      return [...new Set(this.records.map((record) => record[key]))];
    }

    renderChoices() {
      // Black, white, then light-to-warm neutrals ending with peach; other colors keep their order in between.
      const rank = { black: 0, white: 1, ivory: 2, cream: 3, beige: 4, nude: 20, apricot: 21, peach: 22 };
      const order = (color) => rank[color.toLowerCase().trim()] ?? 10;
      [...this.uniqueValues('color')].sort((a, b) => order(a) - order(b)).forEach((color) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'ec-compound-choice ec-compound-color';
        button.dataset.color = color;
        button.title = color;
        button.innerHTML = `<span class="ec-compound-dot" aria-hidden="true"></span><span class="ec-compound-color-label"></span>`;
        button.querySelector('.ec-compound-dot').style.setProperty('--ec-option-color', this.colorValue(color));
        button.querySelector('.ec-compound-color-label').textContent = color;
        button.addEventListener('click', () => this.chooseColor(color));
        this.colorContainer.appendChild(button);
      });

      this.uniqueValues('style').forEach((style) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'ec-compound-choice ec-compound-style';
        button.dataset.style = style;
        button.textContent = style;
        button.addEventListener('click', () => this.chooseStyle(style));
        this.styleContainer.appendChild(button);
      });
    }

    chooseColor(color) {
      const current = this.currentRecord();
      const record =
        this.records.find((candidate) => candidate.color === color && candidate.style === current?.style && candidate.available) ||
        this.records.find((candidate) => candidate.color === color && candidate.available);
      if (record) record.input.click();
    }

    chooseStyle(style) {
      const current = this.currentRecord();
      const record = this.records.find(
        (candidate) => candidate.color === current?.color && candidate.style === style && candidate.available,
      );
      if (record) record.input.click();
    }

    currentRecord() {
      return this.records.find((record) => record.input.checked) || this.records.find((record) => record.available);
    }

    sync() {
      const current = this.currentRecord();
      if (!current) return;

      this.querySelectorAll('[data-color]').forEach((button) => {
        const selected = button.dataset.color === current.color;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
        button.disabled = !this.records.some((record) => record.color === button.dataset.color && record.available);
      });

      this.querySelectorAll('[data-style]').forEach((button) => {
        const selected = button.dataset.style === current.style;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
        button.disabled = !this.records.some(
          (record) => record.color === current.color && record.style === button.dataset.style && record.available,
        );
      });
    }

    colorValue(color) {
      const colors = {
        black: '#151515',
        white: '#ffffff',
        beige: '#d8c4a7',
        ivory: '#fffff0',
        cream: '#f5ead6',
        nude: '#d7ad91',
        red: '#9b1f3f',
        burgundy: '#6f1d36',
        wine: '#6f1d36',
        pink: '#e7a6b5',
        purple: '#714b87',
        blue: '#315f91',
        'sky-blue': '#87bcd9',
        'light-blue': '#87bcd9',
        green: '#436b4d',
        yellow: '#e6c552',
        orange: '#d9793e',
        brown: '#704b3a',
        gray: '#8a8a8a',
        grey: '#8a8a8a',
        silver: '#c4c7ca',
        gold: '#c7a24a',
        apricot: '#e6b894',
        'floral-apricot': '#d9c7ba',
      };
      const key = color.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      return colors[key] || '#d8d2cc';
    }
  }

  customElements.define('compound-variant-picker', CompoundVariantPicker);
}
